import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type CreateHousePointAdjustmentInput,
  type HousePointAdjustmentsFilters,
  type UpdateHousePointAdjustmentInput,
  housePointsApi,
} from '../house-points';

export const housePointsKeys = {
  all: ['admin', 'house-points'] as const,
  list: (filters: HousePointAdjustmentsFilters) => [...housePointsKeys.all, 'list', filters] as const,
};

// House adjustments change the combined house standing, so also refresh sand-dollar stats.
function invalidate(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: housePointsKeys.all });
  void qc.invalidateQueries({ queryKey: ['admin', 'sand-dollars'] });
}

export function useHousePointsQuery(filters: HousePointAdjustmentsFilters = {}) {
  return useQuery({ queryKey: housePointsKeys.list(filters), queryFn: () => housePointsApi.list(filters) });
}

export function useCreateHousePointMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateHousePointAdjustmentInput) => housePointsApi.create(data),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateHousePointMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateHousePointAdjustmentInput }) =>
      housePointsApi.update(id, data),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteHousePointMutation() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => housePointsApi.delete(id), onSuccess: () => invalidate(qc) });
}
